import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../../../features/auth';
import EnvironmentalDataWidget from './EnvironmentalDataWidget';
import {
  isManageActive,
  isNavItemActive,
  mainNav,
  manageNav,
  manageParent,
} from './navConfig';
import { ROUTES } from '../../../config/links';

const TOPBAR_BG = '#2241DD';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isDesktop;
}

function navButtonClass(active: boolean): string {
  return [
    'inline-flex items-center gap-1.5 rounded-full px-[0.7rem] py-[0.6rem] text-white',
    'text-[0.78rem] leading-none',
    'transition-all duration-150 ease-out active:scale-[0.96]',
    active ? 'bg-white/[0.18] font-bold' : 'font-medium hover:bg-white/[0.12]',
  ].join(' ');
}

const Topbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isLoading, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const isDesktop = useIsDesktop();

  const [manageOpen, setManageOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileManageOpen, setMobileManageOpen] = useState(false);

  const manageRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const manageActive = isManageActive(location.pathname);
  const ManageIcon = manageParent.icon;
  const userInitial =
    user?.firstName?.charAt(0)?.toUpperCase() ||
    user?.name?.charAt(0)?.toUpperCase() ||
    'U';
  const displayName = user?.name || 'User';

  useEffect(() => {
    if (manageActive) setMobileManageOpen(true);
  }, [manageActive]);

  useEffect(() => {
    setManageOpen(false);
    setUserMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (manageRef.current && !manageRef.current.contains(target)) {
        setManageOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
  };

  return (
    <>
      <header
        className="sticky top-0 z-50 flex items-center gap-2 px-4 md:px-6"
        style={{
          backgroundColor: TOPBAR_BG,
          minHeight: isDesktop ? 60 : 56,
        }}
      >
        {/* Mobile hamburger */}
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white transition-all duration-150 hover:bg-white/12 active:scale-95 md:hidden"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        {/* Brand */}
        <button
          type="button"
          onClick={() => navigate(ROUTES.ROOT)}
          className="flex items-center gap-2 rounded-full pr-1 text-left transition-all duration-150 hover:bg-white/10 active:scale-[0.98]"
          aria-label="CalibraOne home"
        >
          <img
            src="/logo.svg"
            alt=""
            className="h-[34px] w-[34px] object-contain"
            style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35))' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <span
            className="hidden text-base font-extrabold tracking-[-0.02em] text-white sm:inline"
            style={{ letterSpacing: '-0.02em' }}
          >
            Calibra<span style={{ color: '#FDB022' }}>One</span>
          </span>
        </button>

        <div
          className="hidden h-6 w-px sm:block"
          style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
        />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-2 md:flex">
          {mainNav.map((item) => {
            const Icon = item.icon;
            const active = isNavItemActive(location.pathname, item);
            return (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.href === ROUTES.ROOT}
                className={navButtonClass(active)}
              >
                <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                {item.name}
              </NavLink>
            );
          })}

          {isAdmin && (
            <div className="relative" ref={manageRef}>
              <button
                type="button"
                className={navButtonClass(manageActive || manageOpen)}
                aria-expanded={manageOpen}
                aria-haspopup="true"
                onClick={() => {
                  setManageOpen((v) => !v);
                  setUserMenuOpen(false);
                }}
              >
                <ManageIcon className="h-4 w-4 shrink-0" strokeWidth={2} />
                {manageParent.name}
                <ChevronDown
                  className={`h-3.5 w-3.5 shrink-0 opacity-70 transition-transform duration-200 ease-out ${manageOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {manageOpen && (
                <div
                  className="topbar-menu-enter absolute left-0 top-full z-50 mt-1.5 min-w-[280px] overflow-hidden rounded-[10px] border border-slate-200 bg-white py-1"
                  style={{
                    boxShadow:
                      '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.07)',
                  }}
                >
                  {manageNav.map((item) => {
                    const Icon = item.icon;
                    const active = isNavItemActive(location.pathname, item);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setManageOpen(false)}
                        className={[
                          'mx-1.5 my-0.5 flex items-start gap-2.5 rounded-md px-2.5 py-2',
                          'transition-colors duration-150',
                          active
                            ? 'bg-[#EFF6FF] text-[#2563EB]'
                            : 'text-slate-800 hover:bg-slate-50',
                        ].join(' ')}
                      >
                        <Icon
                          className={`mt-0.5 h-4 w-4 shrink-0 ${active ? 'text-[#2563EB]' : 'text-slate-500'}`}
                          strokeWidth={2}
                        />
                        <span className="min-w-0 flex-1 text-left">
                          <span
                            className={`block text-[0.8rem] font-semibold leading-snug ${
                              active ? 'text-[#2563EB]' : 'text-slate-900'
                            }`}
                          >
                            {item.name}
                          </span>
                          {item.description && (
                            <span
                              className={`mt-0.5 block text-[0.72rem] font-normal leading-snug ${
                                active ? 'text-[#2563EB]/80' : 'text-slate-500'
                              }`}
                            >
                              {item.description}
                            </span>
                          )}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="flex-1" />

        {/* Right: env widget + user */}
        <div className="flex items-center gap-2">
          <EnvironmentalDataWidget variant="topbar" />

          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => {
                setUserMenuOpen((v) => !v);
                setManageOpen(false);
              }}
              className="flex items-center gap-2.5 rounded-full border border-white/30 bg-transparent text-white shadow-none transition-colors duration-150 ease-out hover:bg-white/10 active:bg-white/[0.14]"
              style={{ boxShadow: 'none', padding: '6px 14px' }}
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
            >
              <div
                className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[0.8125rem] font-bold text-white"
                style={{ backgroundColor: '#2563EB' }}
              >
                {userInitial}
              </div>
              <div className="hidden min-w-0 text-left sm:block">
                <div className="truncate text-[0.8125rem] font-semibold text-white" style={{ lineHeight: 1.43 }}>
                  {displayName}
                </div>
                {user?.email && (
                  <div className="truncate text-[0.7rem] text-white/70" style={{ lineHeight: 1.66 }}>
                    {user.email}
                  </div>
                )}
              </div>
              <ChevronDown
                className={`hidden h-4 w-4 shrink-0 text-white/70 transition-transform duration-200 ease-out sm:block ${userMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {userMenuOpen && (
              <div
                className="topbar-menu-enter-right absolute right-0 top-full z-50 mt-1.5 min-w-[220px] overflow-hidden rounded-[10px] border border-slate-200 bg-white py-1"
                style={{
                  boxShadow:
                    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.07)',
                }}
              >
                <div className="mb-0.5 border-b border-slate-200 px-4 py-3">
                  <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
                  {user?.email && (
                    <p className="mt-0.5 truncate text-xs text-slate-500">{user.email}</p>
                  )}
                  {user?.roles && user.roles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {user.roles.map((role: string) => (
                        <span
                          key={role}
                          className="inline-block rounded-md bg-blue-600 px-2 py-0.5 text-xs font-semibold capitalize text-white"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isLoading}
                  className="mx-1.5 my-0.5 flex w-[calc(100%-12px)] items-center gap-2 rounded-md px-2.5 py-2 text-left text-[0.875rem] font-medium text-slate-800 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-50"
                >
                  <LogOut className="h-4 w-4 shrink-0 text-slate-500" strokeWidth={2} />
                  {isLoading ? 'Signing out...' : 'Sign out'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" aria-hidden="true">
          <div
            className="topbar-backdrop-enter absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="topbar-drawer-enter absolute inset-y-0 left-0 flex w-[280px] flex-col text-white shadow-xl"
            style={{ backgroundColor: TOPBAR_BG }}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <span className="text-base font-extrabold tracking-[-0.02em]">
                Calibra<span style={{ color: '#FDB022' }}>One</span>
              </span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="rounded-full p-2 text-white/80 transition-all duration-150 hover:bg-white/10 hover:text-white active:scale-95"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-3">
              {mainNav.map((item) => {
                const Icon = item.icon;
                const active = isNavItemActive(location.pathname, item);
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.href === ROUTES.ROOT}
                    onClick={() => setMobileOpen(false)}
                    className={[
                      'mb-1 flex items-center gap-3 rounded-full px-3 py-2.5 text-sm text-white',
                      'transition-all duration-150 active:scale-[0.98]',
                      active ? 'bg-white/[0.18] font-bold' : 'font-medium hover:bg-white/[0.12]',
                    ].join(' ')}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {item.name}
                  </NavLink>
                );
              })}

              {isAdmin && (
                <div className="mt-2 border-t border-white/10 pt-2">
                  <button
                    type="button"
                    onClick={() => setMobileManageOpen((v) => !v)}
                    className={[
                      'mb-1 flex w-full items-center justify-between rounded-full px-3 py-2.5 text-sm text-white',
                      'transition-all duration-150 active:scale-[0.98]',
                      manageActive || mobileManageOpen
                        ? 'bg-white/[0.18] font-bold'
                        : 'font-medium hover:bg-white/[0.12]',
                    ].join(' ')}
                  >
                    <span className="flex items-center gap-3">
                      <ManageIcon className="h-5 w-5 shrink-0" />
                      {manageParent.name}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 opacity-70 transition-transform duration-200 ease-out ${mobileManageOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileManageOpen && (
                    <div className="topbar-submenu-enter ml-2 space-y-0.5 border-l border-white/15 pl-2">
                      {manageNav.map((item) => {
                        const Icon = item.icon;
                        const active = isNavItemActive(location.pathname, item);
                        return (
                          <NavLink
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={[
                              'flex items-start gap-3 rounded-full px-3 py-2 text-white',
                              'transition-all duration-150 active:scale-[0.98]',
                              active
                                ? 'bg-white/[0.18] font-bold'
                                : 'font-medium hover:bg-white/[0.12]',
                            ].join(' ')}
                          >
                            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                            <span className="min-w-0 flex-1 text-left">
                              <span className="block text-sm leading-snug">{item.name}</span>
                              {item.description && (
                                <span className="mt-0.5 block text-[0.72rem] font-normal leading-snug text-white/70">
                                  {item.description}
                                </span>
                              )}
                            </span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Topbar;
